import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "services/api.service";
import { WorkOrder, type WorkOrderFormSubmitPayload } from "types/workOrder";

export type WorkOrderApiPayload = Partial<WorkOrder> & WorkOrderFormSubmitPayload;

export default function useWorkOrders() {
  const [searchParams] = useSearchParams();
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [workOrder, setWorkOrder] = useState<WorkOrder | null>(null);
  const [meta, setMeta] = useState<{ total_pages: number; count: number }>({ total_pages: 0, count: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const params = useParams();
  const navigate = useNavigate();

  const paramsString = useMemo(() => searchParams.toString(), [searchParams]);

  const getWorkOrders = useCallback(() => {
    setIsLoading(true);
    api
      .get<{ tickets: WorkOrder[]; meta: { total_pages: number; count: number } }>(`/tickets?${paramsString}`)
      .then((res) => {
        setWorkOrders(res.data.tickets || []);
        setMeta(res.data.meta || { total_pages: 0, count: 0 });
        setIsLoading(false);
      })
      .catch(() => {
        setErrorMessage("Failed to load work orders.");
        setIsLoading(false);
      });
  }, [paramsString]);

  useEffect(() => {
    getWorkOrders();
  }, [getWorkOrders]);

  const id = params.uuid || params.id;
  useEffect(() => {
    if (id) {
      api
        .get<{ ticket: WorkOrder }>(`/tickets/${id}`)
        .then((res) => setWorkOrder(res.data.ticket))
        .catch(() => setErrorMessage("Failed to load work order."));
    } else {
      setWorkOrder(null);
    }
  }, [id]);

  const createWorkOrder = useCallback(
    (data: WorkOrderApiPayload) => {
      return api.post<{ ticket: WorkOrder }>("/tickets", { ticket: data }).then((res) => {
        navigate("/work_orders");
        return res.data.ticket;
      });
    },
    [navigate]
  );

  const updateWorkOrder = useCallback(
    (data: WorkOrderApiPayload) => {
      const uuid = data.uuid || id;
      if (!uuid) return Promise.reject(new Error("No id"));
      return api.put(`/tickets/${uuid}`, { ticket: data }).then(() => {
        navigate("/work_orders");
      });
    },
    [id, navigate]
  );

  const deleteWorkOrder = useCallback(
    (uuid: string) => {
      return api.delete(`/tickets/${uuid}`).then(() => {
        getWorkOrders();
      });
    },
    [getWorkOrders]
  );

  return {
    workOrders,
    workOrder,
    meta,
    isLoading,
    errorMessage,
    getWorkOrders,
    createWorkOrder,
    updateWorkOrder,
    deleteWorkOrder,
  };
}
